import apiClient from "./api";

export interface SaleItem {
  id: number;
  sale_id: number;
  product_id: number;
  product_code: string;
  product_name: string;
  unit_price: number;
  price: number;
  quantity: number;
  subtotal: number;
  total: number;
}

export interface SalePayment {
  id: number;
  sale_id: number;
  reference_no?: string;
  method_id?: number | null;
  paid_by: string;
  amount: number;
  note?: string | null;
  payment_status?: string;
  created_at?: string;
  updated_at?: string;
  method?: {
    id: number;
    code: string;
    name: string;
  } | null;
}

export interface Sale {
  id: number;
  reference_no: string;
  customer_id: number;
  customer_name: string;
  warehouse_id: number;
  warehouse_name: string;
  biller_id: number;
  biller_name: string;
  total: number;
  discount: number;
  tax?: number;
  shipping: number;
  grand_total: number;
  paid: number;
  due: number;
  status: string;
  payment_status: string;
  payment_method?: string;
  paid_by: string;
  discount_type?: "fixed" | "percentage";
  coupon_discount?: number;
  coupon_type?: "fixed" | "percentage";
  pos?: boolean | number;
  date: string;
  created_at: string;
  items: SaleItem[];
  payments?: SalePayment[];
  customer?: {
    id: number;
    name: string;
    code: string;
  };
  warehouse?: {
    id: number;
    name: string;
    code: string;
  };
  biller?: {
    id: number;
    name: string;
    code: string;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

export interface SalesListParams {
  customer_id?: number;
  warehouse_id?: number;
  reference_no?: string;
  status?: string;
  payment_status?: string;
  q?: string;
  search?: string;
  pos?: number;
  per_page?: number;
  page?: number;
}

export interface SalePaymentPayload {
  amount: number;
  paid_by: string;
  method_id?: number | null;
  note?: string;
}

const salesService = {
  async getSales(params?: SalesListParams): Promise<PaginatedResponse<Sale>> {
    const response = await apiClient.get("/sales", { params });
    return response.data.data;
  },

  async getSaleDetails(id: number): Promise<Sale> {
    const response = await apiClient.get(`/sales/${id}`);
    return response.data.data;
  },

  async deleteSale(id: number): Promise<void> {
    await apiClient.delete(`/sales/${id}`);
  },

  async createSale(data: any): Promise<Sale> {
    const response = await apiClient.post("/sales", data);
    return response.data.data ?? response.data;
  },

  async updateSale(id: number, data: any): Promise<Sale> {
    const response = await apiClient.put(`/sales/${id}`, data);
    return response.data.data ?? response.data;
  },

  async addPayment(id: number, data: SalePaymentPayload) {
    const response = await apiClient.post(`/sales/${id}/payments`, data);
    return response.data;
  },

  async updatePayment(id: number, paymentId: number, data: SalePaymentPayload) {
    const response = await apiClient.put(`/sales/${id}/payments/${paymentId}`, data);
    return response.data;
  },

  async deletePayment(id: number, paymentId: number) {
    const response = await apiClient.delete(`/sales/${id}/payments/${paymentId}`);
    return response.data;
  },

  async getNextReference(): Promise<string> {
    const response = await apiClient.get("/sales/next-reference");
    return response.data.data ?? response.data;
  }
};

export default salesService;
